/**
 * LeasingPrehledy — Phase 6.6 leasing reports workspace.
 *
 * "Leasing – Přehledy". Four month-scoped reports, each backed by a `/v1/leasing/prehled/*` endpoint:
 *   - **Přehled splátek** (`/prehled/splatky`): installments due in the month,
 *     with the legacy DPH gross-up (`dph === 'Ne'` rows are net → multiply by
 *     `1 + vatRate/100`). Totals bez/vč. DPH surfaced in CrownMeta.
 *   - **Smluv k ukončení** (`/prehled/smlouvy-ukonceni`): contracts ending.
 *   - **Přehled úvěrů** (`/prehled/uvery`): loan payments due (spl_uv).
 *   - **Končící úvěry** (`/prehled/uvery-ukonceni`): loans ending.
 *
 * Layout: Shell · AppRail · Crown (breadcrumb · "Leasing, přehledy." ·
 * CrownMeta totals · report-selector Subtabs) · Toolband (month picker +
 * Tisknout) · `<DataTable>` of the active report. "Tisknout" navigates to the
 * dedicated print route ([LeasingPrehledTisk.tsx](./LeasingPrehledTisk.tsx)),
 * which renders just the list as a clean A4 sheet — printing the live
 * workspace dragged the chrome onto the page and clipped the scroll viewport.
 *
 * Report selector + month live in the URL (`?report=&mesic=`).
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Marker } from '@/components/Marker';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { Toolband } from '@/components/Toolband';
import { formatInt } from '@/lib/format';
import {
  useSplatkyQuery,
  useSmlouvyUkonceniQuery,
  useUveryQuery,
  useUveryUkonceniQuery,
  type PrehledReport,
  type PrehledSplatka,
  type PrehledSmlouvaUkonceni,
  type PrehledUver,
  type PrehledUverUkonceni,
} from './api';
import { clientLine, currentMonth, dayOfMonth, shortDate } from './helpers';

const REPORT_OPTIONS: { value: PrehledReport; label: string }[] = [
  { value: 'splatky', label: 'Přehled splátek' },
  { value: 'smlouvy-ukonceni', label: 'Smluv k ukončení' },
  { value: 'uvery', label: 'Přehled úvěrů' },
  { value: 'uvery-ukonceni', label: 'Končící úvěry' },
];

function isTerminated(predcasne_ukonceno: string | null): boolean {
  if (!predcasne_ukonceno) return false;
  const t = Date.parse(predcasne_ukonceno);
  return !Number.isNaN(t) && t > Date.parse('2000-01-01T00:00:00Z');
}

export function LeasingPrehledy() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/leasing/prehledy' });
  const report: PrehledReport = search.report ?? 'splatky';
  const mesic = search.mesic ?? currentMonth();

  const setReport = (next: PrehledReport) => {
    navigate({
      to: '/leasing/prehledy',
      search: { report: next === 'splatky' ? undefined : next, mesic: search.mesic },
      replace: true,
    });
  };

  const setMesic = (next: string) => {
    navigate({
      to: '/leasing/prehledy',
      search: { report: search.report, mesic: next || undefined },
      replace: true,
    });
  };

  // Only the active report's query fires.
  const splatkyQuery = useSplatkyQuery(mesic, report === 'splatky');
  const smlouvyQuery = useSmlouvyUkonceniQuery(mesic, report === 'smlouvy-ukonceni');
  const uveryQuery = useUveryQuery(mesic, report === 'uvery');
  const uveryUkonceniQuery = useUveryUkonceniQuery(mesic, report === 'uvery-ukonceni');

  const activeQuery =
    report === 'splatky'
      ? splatkyQuery
      : report === 'smlouvy-ukonceni'
        ? smlouvyQuery
        : report === 'uvery'
          ? uveryQuery
          : uveryUkonceniQuery;
  const isLoading = activeQuery.isLoading;
  const isError = activeQuery.isError;

  const splatky = (report === 'splatky' ? splatkyQuery.data?.splatky : []) ?? [];
  const vatRate = splatkyQuery.data?.vatRate ?? 21;
  const dphMult = 1 + vatRate / 100;
  const smlouvy = (report === 'smlouvy-ukonceni' ? smlouvyQuery.data?.smlouvy : []) ?? [];
  const uvery = (report === 'uvery' ? uveryQuery.data?.uvery : []) ?? [];
  const uveryUkonceni =
    (report === 'uvery-ukonceni' ? uveryUkonceniQuery.data?.uvery : []) ?? [];

  // Gross-up: a `dph === 'Ne'` row stores the net splátka, so multiply to get
  // the VAT-inclusive figure; otherwise the stored splátka already includes VAT.
  const grossSplatka = (s: PrehledSplatka): number =>
    s.dph === 'Ne' ? s.splatka * dphMult : s.splatka;

  const splatkyTotals = useMemo(() => {
    let bez = 0;
    let vc = 0;
    for (const s of splatky) {
      const gross = grossSplatka(s);
      bez += gross / dphMult;
      vc += gross;
    }
    return { bez, vc };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splatky, dphMult]);

  const uveryTotal = useMemo(
    () => uvery.reduce((sum, u) => sum + (Number(u.spl_uv) || 0), 0),
    [uvery],
  );

  const meta = (() => {
    switch (report) {
      case 'splatky':
        return [
          { label: 'Splátek', value: formatInt(splatky.length) },
          { label: 'Celkem bez DPH', value: `${formatInt(splatkyTotals.bez)} Kč` },
          { label: 'Celkem vč. DPH', value: `${formatInt(splatkyTotals.vc)} Kč` },
        ];
      case 'smlouvy-ukonceni':
        return [{ label: 'Smluv', value: formatInt(smlouvy.length) }];
      case 'uvery':
        return [
          { label: 'Úvěrů', value: formatInt(uvery.length) },
          { label: 'Celkem', value: `${formatInt(uveryTotal)} Kč` },
        ];
      case 'uvery-ukonceni':
        return [{ label: 'Úvěrů', value: formatInt(uveryUkonceni.length) }];
    }
  })();

  const table = (() => {
    if (report === 'splatky') {
      return (
        <DataTable<PrehledSplatka>
          aria-label="Přehled splátek"
          variant="bordered"
          columns={splatkyColumns(grossSplatka, dphMult)}
          data={isLoading ? [] : splatky}
          getRowId={(s) => String(s.id_smlouvy)}
          empty={<ReportEmpty isLoading={isLoading} isError={isError} />}
          className="min-h-0 flex-1"
        />
      );
    }
    if (report === 'smlouvy-ukonceni') {
      return (
        <DataTable<PrehledSmlouvaUkonceni>
          aria-label="Smlouvy k ukončení"
          variant="bordered"
          columns={smlouvyColumns()}
          data={isLoading ? [] : smlouvy}
          getRowId={(s) => String(s.id_smlouvy)}
          empty={<ReportEmpty isLoading={isLoading} isError={isError} />}
          className="min-h-0 flex-1"
        />
      );
    }
    if (report === 'uvery') {
      return (
        <DataTable<PrehledUver>
          aria-label="Přehled úvěrů"
          variant="bordered"
          columns={uveryColumns()}
          data={isLoading ? [] : uvery}
          getRowId={(u) => String(u.id_smlouvy)}
          empty={<ReportEmpty isLoading={isLoading} isError={isError} />}
          className="min-h-0 flex-1"
        />
      );
    }
    return (
      <DataTable<PrehledUverUkonceni>
        aria-label="Končící úvěry"
        variant="bordered"
        columns={uveryUkonceniColumns()}
        data={isLoading ? [] : uveryUkonceni}
        getRowId={(u) => `${u.cislo_uveru}|${u.uver_spol}|${u.datum_konec}`}
        empty={<ReportEmpty isLoading={isLoading} isError={isError} />}
        className="min-h-0 flex-1"
      />
    );
  })();

  return (
    <Shell rail={<AppRail active="leasing" />}>
      <Crown>
        <Breadcrumb
          segments={[
            { label: <Link to="/leasing">Leasing</Link> },
            { label: 'Přehledy' },
          ]}
        />
        <Crown.Row className="mt-3">
          <DisplayHeading>Leasing</DisplayHeading>
          <CrownMeta items={meta} />
        </Crown.Row>
        <Crown.Row className="mt-3">
          <Subtabs<PrehledReport>
            aria-label="Typ přehledu"
            items={REPORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            value={report}
            onChange={setReport}
          />
        </Crown.Row>
      </Crown>

      <Toolband>
        <label className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Měsíc
          <input
            type="month"
            value={mesic}
            onChange={(e) => setMesic(e.target.value)}
            data-testid="prehledy-mesic"
            className="border border-line bg-surface px-2 py-1.5 text-[12px] font-mono tabular-nums tracking-[-0.02em] text-ink focus:border-accent focus:outline-none"
          />
        </label>
        <Toolband.Spacer />
        <Button
          size="sm"
          variant="secondary"
          icon={<Icon name="download" size={12} />}
          onClick={() =>
            navigate({
              to: '/leasing/prehledy/tisk',
              search: { report: report === 'splatky' ? undefined : report, mesic },
              // Full document load — see NabidkaDetail print button.
              reloadDocument: true,
            })
          }
        >
          Tisknout
        </Button>
      </Toolband>

      <div data-testid="leasing-prehledy-page" className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3 max-sm:px-0 max-sm:pb-4 max-sm:pt-1">
        {table}
      </div>
    </Shell>
  );
}

// ============================================================================
// Columns
// ============================================================================

const SPLATKY_COLUMNS = '90px minmax(0,1.4fr) minmax(0,1.2fr) 140px 140px 110px';

function splatkyColumns(
  gross: (s: PrehledSplatka) => number,
  dphMult: number,
): DataTableColumn<PrehledSplatka>[] {
  const w = SPLATKY_COLUMNS.split(' ');
  return [
    {
      id: 'cislo',
      priority: 'meta',
      header: 'Č. smlouvy',
      width: w[0],
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">{s.id_smlouvy}</span>
      ),
    },
    {
      id: 'klient',
      priority: 'primary',
      header: 'Klient',
      width: w[1],
      cell: (s) => (
        <span className="flex min-w-0 items-center gap-2 truncate font-semibold tracking-[-0.01em] text-ink">
          {isTerminated(s.predcasne_ukonceno) && <Marker tone="warn">Ukončeno</Marker>}
          {clientLine(s)}
        </span>
      ),
    },
    {
      id: 'vin',
      priority: 'secondary',
      header: 'VIN',
      width: w[2],
      cell: (s) => (
        <span className="truncate font-mono text-[11px] tracking-[-0.02em] text-ink-2">
          {s.vin || '—'}
        </span>
      ),
    },
    {
      id: 'bez',
      priority: 'meta',
      header: 'Splátka bez DPH',
      width: w[3],
      align: 'right',
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink-2">
          {formatInt(gross(s) / dphMult)} <span className="text-ink-3">Kč</span>
        </span>
      ),
    },
    {
      id: 'vc',
      priority: 'meta',
      header: 'Splátka vč. DPH',
      width: w[4],
      align: 'right',
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">
          {formatInt(gross(s))} <span className="text-ink-3">Kč</span>
        </span>
      ),
    },
    {
      id: 'fakturace',
      priority: 'meta',
      header: 'Fakturace',
      width: w[5],
      align: 'right',
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink-3">
          {dayOfMonth(s.datum_od) || '—'}
        </span>
      ),
    },
  ];
}

const SMLOUVY_COLUMNS = '90px minmax(0,1.6fr) minmax(0,1.4fr) 140px';

function smlouvyColumns(): DataTableColumn<PrehledSmlouvaUkonceni>[] {
  const w = SMLOUVY_COLUMNS.split(' ');
  return [
    {
      id: 'cislo',
      priority: 'meta',
      header: 'Č. smlouvy',
      width: w[0],
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">{s.id_smlouvy}</span>
      ),
    },
    {
      id: 'klient',
      priority: 'primary',
      header: 'Klient',
      width: w[1],
      cell: (s) => (
        <span className="flex min-w-0 items-center gap-2 truncate font-semibold tracking-[-0.01em] text-ink">
          {isTerminated(s.predcasne_ukonceno) && <Marker tone="warn">Ukončeno</Marker>}
          {clientLine(s)}
        </span>
      ),
    },
    {
      id: 'vin',
      priority: 'secondary',
      header: 'VIN',
      width: w[2],
      cell: (s) => (
        <span className="truncate font-mono text-[11px] tracking-[-0.02em] text-ink-2">
          {s.vin || '—'}
        </span>
      ),
    },
    {
      id: 'datum',
      priority: 'meta',
      header: 'Datum ukončení',
      width: w[3],
      align: 'right',
      cell: (s) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink-2">
          {shortDate(s.datum_do) || '—'}
        </span>
      ),
    },
  ];
}

const UVERY_COLUMNS = '90px minmax(0,1.3fr) minmax(0,1.2fr) 120px 130px 100px';

function uveryColumns(): DataTableColumn<PrehledUver>[] {
  const w = UVERY_COLUMNS.split(' ');
  return [
    {
      id: 'cislo',
      priority: 'meta',
      header: 'Č. smlouvy',
      width: w[0],
      cell: (u) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">{u.id_smlouvy}</span>
      ),
    },
    {
      id: 'klient',
      priority: 'primary',
      header: 'Klient',
      width: w[1],
      cell: (u) => (
        <span className="truncate font-semibold tracking-[-0.01em] text-ink">{clientLine(u)}</span>
      ),
    },
    {
      id: 'spol',
      priority: 'secondary',
      header: 'Úvěrová společnost',
      width: w[2],
      cell: (u) => <span className="truncate text-[12px] text-ink-2">{u.uver_spol || '—'}</span>,
    },
    {
      id: 'cislo_uveru',
      priority: 'secondary',
      header: 'Č. úvěru',
      width: w[3],
      cell: (u) => (
        <span className="truncate font-mono text-[11px] tracking-[-0.02em] text-ink-2">
          {u.cislo_uveru || '—'}
        </span>
      ),
    },
    {
      id: 'platba',
      priority: 'meta',
      header: 'Platba bez DPH',
      width: w[4],
      align: 'right',
      cell: (u) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink">
          {formatInt(u.spl_uv)} <span className="text-ink-3">Kč</span>
        </span>
      ),
    },
    {
      id: 'datum',
      priority: 'meta',
      header: 'Platba',
      width: w[5],
      align: 'right',
      cell: (u) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink-3">
          {dayOfMonth(u.datum_pocatek) || '—'}
        </span>
      ),
    },
  ];
}

const UVERY_UKONCENI_COLUMNS = 'minmax(0,1fr) minmax(0,1.4fr) 160px';

function uveryUkonceniColumns(): DataTableColumn<PrehledUverUkonceni>[] {
  const w = UVERY_UKONCENI_COLUMNS.split(' ');
  return [
    {
      id: 'cislo_uveru',
      priority: 'primary',
      header: 'Č. úvěru',
      width: w[0],
      cell: (u) => (
        <span className="truncate font-mono text-[11.5px] tracking-[-0.02em] text-ink">
          {u.cislo_uveru || '—'}
        </span>
      ),
    },
    {
      id: 'spol',
      priority: 'secondary',
      header: 'Úvěrová společnost',
      width: w[1],
      cell: (u) => <span className="truncate text-[12px] text-ink-2">{u.uver_spol || '—'}</span>,
    },
    {
      id: 'datum',
      priority: 'meta',
      header: 'Datum ukončení',
      width: w[2],
      align: 'right',
      cell: (u) => (
        <span className="font-mono text-[11.5px] tracking-[-0.02em] text-ink-2">
          {shortDate(u.datum_konec) || '—'}
        </span>
      ),
    },
  ];
}

// ============================================================================
// Empty / loading
// ============================================================================

function ReportEmpty({ isLoading, isError }: { isLoading: boolean; isError: boolean }) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-px p-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <EmptyState
        eyebrow="Chyba"
        title="Data se nepodařilo načíst"
        description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
      />
    );
  }
  return (
    <EmptyState
      eyebrow="Prázdné"
      title="Žádné výsledky"
      description="Pro vybraný měsíc nejsou žádné záznamy. Zkuste jiný měsíc."
    />
  );
}
